import React, { useState, useMemo, useCallback } from 'react';
import { format, parse, isValid } from "date-fns";
import toast from 'react-hot-toast';
import Papa from 'papaparse';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    CircularProgress,
    FormControl,
    MenuItem,
    InputLabel,
    Select,
    Tooltip,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import useAuthCheck from 'hooks/useAuthCheck';
import {
    LAYOUT_HEIGHT,
    DIVERT_RETURN_STATUS,
    EXCLUDED_FLIGHT_STATUS
} from 'state/constants';
import {
    removeItemByValueImmutable,
    convertTimeToDecimal,
} from 'state/helpers';
import {
    countFlightsByField,
    countFlightsBy2Fields,
    countStackedFlightsBy2Fields
} from 'scenes/charts/utils';
import PageHeader from "components/PageHeader";
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlightFilterDialog from 'components/FlightFilterDialog';
import ChartDialog from 'components/chartNivo/ChartDialog';
import PieChart from 'components/chartNivo/PieChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import { boxStyles as styles } from 'components/CustomStyles';
import HelpDialog from './HelpDialog';


const filterFlights = ({ flights, filters }) => {
    if (!Array.isArray(flights)) return [];
    if (!filters) return flights;

    return flights.filter((flight) => {
        const etdStr = flight.etd?.substring(0, 5);
        const minEtd = filters.minEtd;
        const maxEtd = filters.maxEtd;

        if (minEtd && (!etdStr || etdStr < minEtd)) return false;
        if (maxEtd && (!etdStr || etdStr > maxEtd)) return false;

        return Object.entries(filters).every(([key, value]) => {
            if (!value) return true;
            if (key === 'minEtd' || key === 'maxEtd') return true;

            if (key === 'flightDate') {
                const formattedFlightDate = flight[key] ? format(new Date(flight[key]), 'yyyy-MM-dd') : null;
                const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                return formattedFlightDate === formattedFilterDate;
            }

            if (key === 'flightNo') {
                return flight[key] === Number(value);
            }

            if (key === 'sector') {
                return flight[key]?.toLowerCase().includes(value.toLowerCase());
            }

            if (key === 'acType' || key === 'flightReg') {
                return Array.isArray(value) ? value.includes(flight[key]) : flight[key] === value;
            }

            return flight[key] === value;
        });
    });
};

const headerMap = {
    DATE: "flightDate",
    Route: "routeNo",
    FLT: "flightNo",
    CARRIER: "flightCarrier",
    TYPE: "flightType",
    REG: "flightReg",
    AC: "acType",
    DEP: "dep",
    ARR: "arr",
    STD: "std",
    STA: "sta",
    ETD: "etd",
    ETA: "eta",
    TKof: "takeOff",
    TDwn: "touchDown",
    ATD: "atd",
    ATA: "ata",
    BLOCK: "blockHours",
    FLThr: "flightHours",
    ST: "flightStatus",
    DEP_NAME: "depCountryName",
    ARR_NAME: "arrCountryName",
    LF: "loadFactor",
    "Taxi-out": "taxiOut",
    "Taxi-In": "taxiIn",
};


const FlightChart = () => {
    useAuthCheck();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);
    const [selectedChart, setSelectedChart] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);

    const [view, setView] = useState('flightsByFlightDate');
    const [flights, setFlights] = useState([]);

    const filteredFlights = useMemo(() => filterFlights({ flights, filters }), [flights, filters]);

    const chartData = useMemo(() => {
        if (!filteredFlights.length) return {};

        const flightDate = 'flightDate';
        const make = (fn, f1, f2, mode) => fn(filteredFlights, f1, f2, mode);

        return {
            // by Date
            flightsByFlightDate: make(countFlightsByField, flightDate),
            flightsAndBlockHoursByFlightDate: make(countFlightsBy2Fields, flightDate, 'blockHours'),
            flightsByFlightDateSectorType: make(countStackedFlightsBy2Fields, flightDate, 'sectorType'),
            flightsByFlightDateFlightType: make(countStackedFlightsBy2Fields, flightDate, 'flightType'),
            flightsByFlightDateFlightReg: make(countStackedFlightsBy2Fields, flightDate, 'flightReg'),
            flightsByFlightDateACType: make(countStackedFlightsBy2Fields, flightDate, 'acType'),
            flightsByFlightDateCountry: make(countStackedFlightsBy2Fields, flightDate, 'depCountryName'),

            // by Dep
            flightsByDep: make(countFlightsByField, 'dep', 'column'),
            flightsAndBlockHoursByDep: make(countFlightsBy2Fields, 'dep', 'blockHours'),
            flightsByDepSectorType: make(countStackedFlightsBy2Fields, 'dep', 'sectorType'),
            flightsByDepFlightType: make(countStackedFlightsBy2Fields, 'dep', 'flightType'),

            // by Sector
            flightsBySector: make(countFlightsByField, 'sector', 'column'),
            flightsAndBlockHoursBySector: make(countFlightsBy2Fields, 'sector', 'blockHours'),
            flightsBySectorFlightType: make(countStackedFlightsBy2Fields, 'sector', 'flightType'),

            // by FlightType
            flightsByFlightType: make(countFlightsByField, 'flightType', 'pie'),
            flightsAndBlockHoursByFlightType: make(countFlightsBy2Fields, 'flightType', 'blockHours'),

            // by FlightReg
            flightsByFlightReg: make(countFlightsByField, 'flightReg', 'column'),
            flightsAndBlockHoursByFlightReg: make(countFlightsBy2Fields, 'flightReg', 'blockHours'),
            flightsByFlightRegSectorType: make(countStackedFlightsBy2Fields, 'flightReg', 'sectorType'),
            flightsByFlightRegFlightType: make(countStackedFlightsBy2Fields, 'flightReg', 'flightType'),
            flightsByFlightRegCountry: make(countStackedFlightsBy2Fields, 'flightReg', 'depCountryName'),

            // by ACType
            flightsByACType: make(countFlightsByField, 'acType', 'pie'),
            flightsAndBlockHoursByACType: make(countFlightsBy2Fields, 'acType', 'blockHours'),
            flightsByACTypeSectorType: make(countStackedFlightsBy2Fields, 'acType', 'sectorType'),
            flightsByACTypeFlightType: make(countStackedFlightsBy2Fields, 'acType', 'flightType'),
            flightsByACTypeCountry: make(countStackedFlightsBy2Fields, 'acType', 'depCountryName'),

            // by Country
            flightsByCountry: make(countFlightsByField, 'depCountryName', 'pie'),
            flightsAndBlockHoursByCountry: make(countFlightsBy2Fields, 'depCountryName', 'blockHours'),
            flightsByCountrySectorType: make(countStackedFlightsBy2Fields, 'depCountryName', 'sectorType'),
            flightsByCountryFlightType: make(countStackedFlightsBy2Fields, 'depCountryName', 'flightType'),
            flightsByCountryACType: make(countStackedFlightsBy2Fields, 'depCountryName', 'acType'),
        };
    }, [filteredFlights]);

    const filterOptions = useMemo(() => {
        if (!Array.isArray(flights)) {
            return { acTypeOptions: [], flightRegOptions: [], sectorTypeOptions: [], departureTypeOptions: [], arrivalTypeOptions: [] };
        }
        const unique = (field) => [...new Set(flights.map(f => f[field]))].sort();
        return {
            acTypeOptions: unique('acType'),
            flightRegOptions: unique('flightReg'),
            sectorTypeOptions: unique('sectorType'),
            departureTypeOptions: unique('departureType'),
            arrivalTypeOptions: unique('arrivalType'),
        };
    }, [flights]);

    const handleFilterOpen  = useCallback(() => setIsFilterDialogOpen(true), []);
    const handleFilterClose = useCallback(() => setIsFilterDialogOpen(false), []);
    const handleFilterApply = useCallback((filterValues) => setFilters(filterValues), []);

    const handleHelp      = useCallback(() => setHelpOpen(true), []);
    const handleHelpClose = useCallback(() => setHelpOpen(false), []);

    const handleChartOpen = useCallback((chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box height="100%" width="100%" sx={chart.style}>
                    {chart.component}
                </Box>
            </Box>
        );
        setChartOpen(true);
    }, []);

    const handleChartClose = useCallback(() => {
        setSelectedChart(null);
        setChartOpen(false);
    }, []);

    const handleUpload = useCallback(async (event) => {
        const file = event.target.files[0];
        if (!file) {
            toast.error("No file selected!");
            return;
        }

        setError(null);
        setLoading(true);

        try {
            const text = await file.text();
            const lines = text.split("\n").map(l => l.trim());
            let cleaned = lines.slice(2);
            const headerLine = cleaned[0];
            cleaned = cleaned.slice(2);
            cleaned = cleaned.filter((row) => {
                if (!row) return false;
                if (/^,+$/.test(row)) return false;
                if (row.startsWith("Total Record")) return false;
                if (row.startsWith('"Generated on')) return false;
                return true;
            });

            const csvContent = [headerLine, ...cleaned].join("\n");

            Papa.parse(csvContent, {
                header: true,
                skipEmptyLines: true,
                transformHeader: (h) => headerMap[h.trim()] || h.trim(),
                transform: (v) => v.trim(),
                complete: (results) => {
                    let rows = results.data;

                    const parseDate = (dateStr) => {
                        if (!dateStr) return null;
                        const date = parse(dateStr, "dd/MM/yy", new Date());
                        if (!isValid(date)) return null;
                        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
                    };

                    rows = rows.map((r) => ({
                        ...r,
                        flightDate: parseDate(r.flightDate),
                        sector: r.dep + '-' + r.arr,
                        blockHours: convertTimeToDecimal(r.blockHours),
                        blockMinutes: convertTimeToDecimal(r.blockHours) * 60,
                        taxiOut: convertTimeToDecimal(r.taxiOut) * 60,
                        taxiIn: convertTimeToDecimal(r.taxiIn) * 60,
                    }));

                    const processedFlights = removeItemByValueImmutable(rows, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
                        .filter(flight => !DIVERT_RETURN_STATUS.includes(flight.flightStatus));

                    setFlights(processedFlights);
                    toast.success("Uploaded flight schedule successfully!");
                    setLoading(false);
                },
                error: (err) => {
                    toast.error("CSV parsing failed: " + err.message);
                    setLoading(false);
                }
            });
        } catch (err) {
            toast.error(err?.message || "Failed to upload file!");
            setLoading(false);
            setError(err);
        }
    }, []);

    const { startDate, endDate } = useMemo(() => {
        if (!Array.isArray(flights) || flights.length === 0) {
            return { startDate: null, endDate: null };
        }

        const times = flights
            .map(f => new Date(f.flightDate).getTime())
            .filter(t => !Number.isNaN(t));

        if (times.length === 0) return { startDate: null, endDate: null };

        return {
            startDate: new Date(Math.min(...times)),
            endDate: new Date(Math.max(...times)),
        };
    }, [flights]);

    const chartConfigurations = useMemo(() => {
        const period = startDate && endDate
            ? `${format(startDate, 'ddMMMyy')} - ${format(endDate, 'ddMMMyy')}`
            : '';
        const defaultTitle = period ? `Flight ${period}` : 'Flight Chart';

        return {
            flightsByFlightDate: [
                { component: <SortBarMultiLinesChart data={chartData.flightsByFlightDate} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} barTranslateXOffSet={120} lineTranslateXOffSet={0} />, style: {} },
                { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByFlightDate} title={defaultTitle} bottomLegend='Date' leftLegend='Value' digit={0} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByFlightDateSectorType} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByFlightDateFlightType} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} itemWidthOffSet={30} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByFlightDateFlightReg} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} itemWidthOffSet={-15} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByFlightDateACType} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} translateXOffSet={-5} itemWidthOffSet={-30} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByFlightDateCountry} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} translateXOffSet={-15} itemWidthOffSet={-15} />, style: {} },
            ],
            flightsByDep: [
                { component: <SortBarChart data={chartData.flightsByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Flights' digit={0} />, style: {} },
                { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Value' digit={0} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5}/>, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByDepSectorType} title={defaultTitle} bottomLegend='Port' leftLegend='Flights' digit={0} itemWidthOffSet={10} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByDepFlightType} title={defaultTitle} bottomLegend='Port' leftLegend='Flights' digit={0} itemWidthOffSet={35} />, style: {} },
            ],
            flightsBySector: [
                { component: <SortBarChart data={chartData.flightsBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='Flights' digit={0} bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
                { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='Value' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsBySectorFlightType} title={defaultTitle} bottomLegend='Sector' leftLegend='Flights' digit={0} bottomOffSet={10} bottomLegendOffSet={10} itemWidthOffSet={35} />, style: {} },
            ],
            flightsByFlightType: [
                { component: <PieChart data={chartData.flightsByFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Flights' isHorizontalBottomLegend={true} digit={0} itemWidthOffSet={20} />, style: {} },
                { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Value' isHorizontalBottomLegend={!isMobile ? true : undefined} digit={0} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5}/>, style: {} },
            ],
            flightsByFlightReg: [
                { component: <SortBarChart data={chartData.flightsByFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} />, style: {} },
                { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Value' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5}/>, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByFlightRegSectorType} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} itemWidthOffSet={10} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByFlightRegFlightType} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} itemWidthOffSet={35} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByFlightRegCountry} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-15} itemWidthOffSet={-15} />, style: {} },
            ],
            flightsByACType: [
                { component: <PieChart data={chartData.flightsByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={0} itemWidthOffSet={-40} />, style: {} },
                { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Value' digit={0} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByACTypeSectorType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByACTypeFlightType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} itemWidthOffSet={35} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByACTypeCountry} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-15} itemWidthOffSet={-15} />, style: {} },
            ],
            flightsByCountry: [
                { component: <PieChart data={chartData.flightsByCountry} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={0} itemsSpacingOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByCountry} title={defaultTitle} bottomLegend='Country' leftLegend='Value' digit={0} bottomOffSet={25} bottomLegendOffSet={25} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByCountrySectorType} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={0} bottomOffSet={25} bottomLegendOffSet={25} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByCountryFlightType} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={0} bottomOffSet={25} bottomLegendOffSet={25} itemWidthOffSet={35} />, style: {} },
                { component: <SortStackedBarChart data={chartData.flightsByCountryACType} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={0} bottomOffSet={25} bottomLegendOffSet={25} translateXOffSet={-15} itemWidthOffSet={-35} />, style: {} },
            ],
        };
    }, [startDate, endDate, isMobile, chartData]);

    const renderCharts = useCallback((charts, customStyles) => (
        <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12" }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box height="100%" width="100%" sx={chart.style}>
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    ), [handleChartOpen]);


    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <PageHeader />

            <Box
                width="96%"
                display="flex"
                flexDirection="column"
                alignSelf="center"
                py={2}
            >
                <FlexBetween>
                    <Header
                        title="Flight"
                        subtitle={loading ? 'Loading...' : 'List of Charts'}
                    />
                    {!loading && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title="Help" arrow>
                                <span>
                                    <StyledButton onClick={handleHelp}>
                                        <HelpIcon />
                                    </StyledButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Upload daily flight schedule" arrow>
                                <span>
                                    <label htmlFor="upload-file">
                                        <StyledButton component="span">
                                            <FileUploadIcon />
                                        </StyledButton>
                                    </label>
                                </span>
                            </Tooltip>
                            <input
                                id="upload-file"
                                type="file"
                                accept=".csv"
                                style={{ display: 'none' }}
                                onChange={handleUpload}
                            />

                            <FormControl>
                                <InputLabel>View</InputLabel>
                                <Select
                                    label="View"
                                    value={view}
                                    onChange={(e) => setView(e.target.value)}
                                    sx={{
                                        height: '36px',
                                        width: isMobile ? '8rem' : '9rem',
                                    }}
                                >
                                    <MenuItem value="flightsByFlightDate">by Date</MenuItem>
                                    <MenuItem value="flightsByDep">by Port</MenuItem>
                                    <MenuItem value="flightsBySector">by Sector</MenuItem>
                                    <MenuItem value="flightsByFlightType">by Flight Type</MenuItem>
                                    <MenuItem value="flightsByFlightReg">by Flight Reg</MenuItem>
                                    <MenuItem value="flightsByACType">by AC Type</MenuItem>
                                    <MenuItem value="flightsByCountry">by Country</MenuItem>
                                </Select>
                            </FormControl>

                            <Tooltip title="Filter" arrow>
                                <span>
                                    <StyledButton onClick={handleFilterOpen}>
                                        <FilterAltIcon />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </FlexBetween>

                {loading ? (
                    <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                        <Typography color="error">Error: {error.message}</Typography>
                    </Box>
                ) : (
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    >
                        {view in chartConfigurations &&
                            renderCharts(chartConfigurations[view], { height: LAYOUT_HEIGHT })
                        }
                    </Box>
                )}
            </Box>

            <ScrollToTop />

            {isFilterDialogOpen && (
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={filterOptions.acTypeOptions}
                    flightRegOptions={filterOptions.flightRegOptions}
                    sectorTypeOptions={filterOptions.sectorTypeOptions}
                    departureTypeOptions={filterOptions.departureTypeOptions}
                    arrivalTypeOptions={filterOptions.arrivalTypeOptions}
                />
            )}

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleChartClose}
                    chartComponent={selectedChart}
                />
            )}

            {helpOpen && (
                <HelpDialog open={helpOpen} onClose={handleHelpClose} />
            )}
        </Box>
    );
};

export default FlightChart;
